'use client'

import type { Product } from '@maestro/data-layer'

import { useMemo } from 'react'

import { useProductStockDataFetcher } from '@maestro/commerce-blocks/hooks/use-product-stock-data-fetcher'
import {
  buildManyProductsContext,
  reportFalsyIds,
} from '@maestro/commerce-blocks/utils/report-falsy-ids'

export const useManyProductsStock = (
  serverProducts: Product[],
  fetchStockClientSide = false,
  listingType?: string
) => {
  const productIds = useMemo(() => {
    if (!serverProducts.length || !fetchStockClientSide) {
      return []
    }
    const rawIds = serverProducts.map((product) => product.id)
    reportFalsyIds({
      hookName: 'useManyProductsStock',
      listingType,
      rawIds,
      productContext: buildManyProductsContext(serverProducts),
    })
    return rawIds.filter(Boolean)
  }, [fetchStockClientSide, listingType, serverProducts])

  const { productsStockMap, isLoading } = useProductStockDataFetcher(productIds)

  const productsWithStock = useMemo(() => {
    if (!serverProducts.length || !productsStockMap) {
      return serverProducts
    }
    return serverProducts.map((product) => ({
      ...product,
      stock: productsStockMap[product.id] ?? product.stock,
    }))
  }, [serverProducts, productsStockMap])

  return {
    products: productsWithStock,
    isLoading,
  }
}
